import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { ApiError } from '@/api/http';
import type { RenderStats, RenderTicket } from '@/api/types';
import { reportClientError } from '@/errorReporting/reporter';
import { lazyComponent } from '@/app/lazyWithPreload';
import {
  FALLBACK_FRAME_HEIGHT,
  SILENT_FRAME_MS,
  estimateFrameHeight,
  frameCanvas,
  MAX_FRAME_HEIGHT,
  MIN_FRAME_HEIGHT,
  scrollFrameTo,
  type FrameAppearance,
  type FrameFit,
  type FrameFitResult,
} from './frameDocument';
import {
  assertIsolatedFrame,
  fitMessage,
  forceLegacy,
  frameURL,
  ISOLATED_FRAME_SANDBOX,
  newChannel,
  parseFrameMessage,
  plainTextOf,
  type FrameMessage,
} from './isolatedFrame';
import { LinkPreview } from './LinkPreview';
import { linkWarnings, type LinkWarning } from './linkRisk';
import { useFrameFocusRing } from './frameFocus';

// The "Open this link?" dialog is shown only after a link is clicked, so it
// and the dialog shell it uses are a chunk of their own, prefetched at idle
// (#577 follow-up). The Open button is a click on this page, so the window it
// opens is still under a user activation.
export const LinkConfirm = lazyComponent(() => import('./LinkConfirm'), 'LinkConfirm');

/**
 * Where a message body is framed from when the deployment has a user-content
 * origin (ADR 0078), and how to get a render of it there.
 *
 * `mint` is called once per frame the component loads — a document capability
 * is single-use — and must decide the remote-content mode the same way the
 * message read did. `stats` asks, on the app origin, why images that did not
 * load did not load: the frame can see that one failed, never its status.
 */
export interface FrameIsolation {
  contentOrigin: string;
  mint: (signal: AbortSignal) => Promise<RenderTicket>;
  stats: (renderId: string, signal: AbortSignal) => Promise<RenderStats>;
}

export interface IsolatedMessageFrameProps {
  html: string;
  /** Unused here: the server puts the stylesheet in the document it mints. */
  css?: string | undefined;
  /**
   * The appearance to mint the render with (ADR 0081). The page cannot reach
   * into this frame, so a change is a new render, swapped in like "Load
   * images": `isolation.mint` must already ask for it.
   */
  appearance?: FrameAppearance;
  bodyText?: string | undefined;
  frameKey: string;
  messageId?: string;
  isolation: FrameIsolation;
  onOpenLink?: (url: URL) => void;
  onMailto?: (href: string) => void;
  onHeight?: (px: number) => void;
  onSubresources?: (status: {
    requested: number;
    failed: number;
    refused: number;
    svgBlocked: number;
  }) => void;
  fit?: FrameFit;
  onFit?: (status: FrameFitResult & { wide: boolean }) => void;
  /** The page's CSP refused the frame, or the server has no origin: use srcdoc. */
  onLegacy: () => void;
}

/** How long a new document may take to say anything before it is shown anyway. */
const SWAP_TIMEOUT_MS = 10_000;

interface Slot {
  id: number;
  frameKey: string;
  messageId: string;
}

interface SlotStatus {
  height: number;
  sized: boolean;
  loaded: boolean;
  fit?: FrameFitResult & { wide: boolean };
  subresources?: { requested: number; failed: number; refused: number; svgBlocked: number };
}

/** A link waiting on "Open this link?". */
interface PendingLink {
  url: URL;
  warnings: LinkWarning[];
  opener: HTMLElement | null;
}

type SlotEvent =
  | { type: 'size'; height: number; zoom: number; scrollX: boolean; wide: boolean }
  | { type: 'silent' }
  | {
      type: 'loaded';
      requested: number;
      failed: number;
      refused: number;
      svgBlocked: number;
    }
  | { type: 'open'; url: URL; text?: string }
  | { type: 'mailto'; href: string }
  | { type: 'preview'; urls: URL[] }
  | { type: 'failed'; offline: boolean }
  | { type: 'legacy' }
  | { type: 'refused' };

function clampHeight(px: number): number {
  if (!Number.isFinite(px) || px <= 0) return FALLBACK_FRAME_HEIGHT;
  return Math.min(MAX_FRAME_HEIGHT, Math.max(MIN_FRAME_HEIGHT, Math.ceil(px)));
}

function defaultOpenLink(url: URL): void {
  window.open(url.href, '_blank', 'noopener,noreferrer');
}

function isOffline(cause: unknown): boolean {
  if (cause instanceof ApiError) return cause.isOffline;
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

let slotSeq = 0;
function nextSlot(frameKey: string, messageId: string): Slot {
  slotSeq += 1;
  return { id: slotSeq, frameKey, messageId };
}

const NO_PREVIEW: readonly URL[] = [];

/**
 * The isolated renderer (ADR 0078).
 *
 * Holds up to two frames. The shown one is in flow; a new render of the SAME
 * message (Load images, Try again) is loaded in a second frame stacked exactly
 * over it, invisible, and swapped in once it has reported its size and its
 * images have settled — so loading images never blanks the message (#272–#274).
 * It is transparent rather than hidden or off-screen on purpose: engines
 * throttle a cross-origin frame they consider hidden, and a throttled frame
 * never reports. A different message replaces at once: one message's body
 * must never paint under another's.
 */
export function IsolatedMessageFrame({
  html,
  bodyText,
  frameKey,
  messageId = frameKey,
  isolation,
  onOpenLink,
  onMailto,
  onHeight,
  onSubresources,
  fit = 'width',
  onFit,
  onLegacy,
  appearance = 'light',
}: IsolatedMessageFrameProps): JSX.Element {
  const [attempt, setAttempt] = useState(0);
  const renderKey = `${frameKey}\u0000${attempt}\u0000${appearance}`;
  const [slots, setSlots] = useState<{ key: string; shown: Slot; pending?: Slot | undefined }>(() => ({
    key: renderKey,
    shown: nextSlot(renderKey, messageId),
  }));
  const [statuses, setStatuses] = useState<Record<number, SlotStatus>>({});
  const [failure, setFailure] = useState<{ slotId: number; offline: boolean } | undefined>(undefined);
  const [refused, setRefused] = useState(false);
  const [preview, setPreview] = useState<readonly URL[]>(NO_PREVIEW);
  const [confirmLink, setConfirmLink] = useState<PendingLink | undefined>(undefined);

  // Reset on prop change during render, as the srcdoc renderer does.
  if (slots.key !== renderKey) {
    const slot = nextSlot(renderKey, messageId);
    // A different message replaces at once; so does a retry of a render that
    // failed, since there is no document on screen to keep.
    if (slots.shown.messageId !== messageId || failure?.slotId === slots.shown.id) {
      setSlots({ key: renderKey, shown: slot });
      setStatuses({});
      setPreview(NO_PREVIEW);
      setConfirmLink(undefined);
    } else {
      setSlots({ key: renderKey, shown: slots.shown, pending: slot });
    }
    setFailure(undefined);
  }

  const callbacks = useRef({ onOpenLink, onMailto, onHeight, onSubresources, onFit, onLegacy });
  useEffect(() => {
    callbacks.current = { onOpenLink, onMailto, onHeight, onSubresources, onFit, onLegacy };
  });

  const shownRef = useRef(slots.shown.id);
  useEffect(() => {
    shownRef.current = slots.shown.id;
  });

  /*
   * The page's own CSP refusing the frame is the one failure that says "this
   * page cannot frame that origin at all" — a service worker still serving an
   * index.html from before the origin was configured. Nothing a message can
   * do produces it, so it is safe to act on: srcdoc for the rest of the page.
   */
  useEffect(() => {
    const origin = isolation.contentOrigin;
    const onViolation = (event: SecurityPolicyViolationEvent) => {
      const directive = event.effectiveDirective || event.violatedDirective || '';
      if (!/^(frame-src|child-src|default-src)/.test(directive)) return;
      if (!event.blockedURI || !event.blockedURI.startsWith(origin)) return;
      forceLegacy();
      reportClientError({
        kind: 'error',
        message: `message frame: the app shell's CSP refused ${origin}; using the srcdoc renderer`,
      });
      callbacks.current.onLegacy();
    };
    document.addEventListener('securitypolicyviolation', onViolation);
    return () => document.removeEventListener('securitypolicyviolation', onViolation);
  }, [isolation.contentOrigin]);

  const onSlotEvent = useCallback((slotId: number, event: SlotEvent) => {
    const isShown = slotId === shownRef.current;
    switch (event.type) {
      case 'size':
        setStatuses((current) => ({
          ...current,
          [slotId]: {
            ...(current[slotId] ?? { loaded: false }),
            height: clampHeight(event.height),
            sized: true,
            fit: { zoom: event.zoom, scrollX: event.scrollX, wide: event.wide },
          },
        }));
        return;
      case 'silent':
        setStatuses((current) => {
          const was = current[slotId];
          if (was?.sized && was.loaded) return current;
          return {
            ...current,
            [slotId]: {
              ...(was ?? {}),
              height: was?.sized ? was.height : FALLBACK_FRAME_HEIGHT,
              sized: true,
              loaded: true,
            },
          };
        });
        return;
      case 'loaded':
        setStatuses((current) => ({
          ...current,
          [slotId]: {
            ...(current[slotId] ?? { height: MIN_FRAME_HEIGHT, sized: false }),
            loaded: true,
            subresources: {
              requested: event.requested,
              failed: event.failed,
              refused: event.refused,
              svgBlocked: event.svgBlocked,
            },
          },
        }));
        return;
      case 'open':
        if (isShown) {
          // The frame had focus when its link was activated; it gets it back
          // when the confirm closes.
          const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          setConfirmLink({ url: event.url, warnings: linkWarnings(event.url, event.text), opener });
        }
        return;
      case 'mailto':
        if (isShown) callbacks.current.onMailto?.(event.href);
        return;
      case 'preview':
        if (isShown) setPreview(event.urls.length ? event.urls : NO_PREVIEW);
        return;
      case 'failed':
        setFailure({ slotId, offline: event.offline });
        return;
      case 'legacy':
        forceLegacy();
        callbacks.current.onLegacy();
        return;
      case 'refused':
        setRefused(true);
        return;
    }
  }, []);

  /*
   * The pending document's fate, decided during render (the same pattern as
   * the reset above): a failed render is dropped and the shown document stays;
   * one that has reported its size and its settled images, or has had its
   * time, is swapped in, and the statuses of frames that are gone go with it.
   */
  const pending = slots.pending;
  const pendingStatus = pending ? statuses[pending.id] : undefined;
  const pendingReady = pendingStatus?.sized === true && pendingStatus.loaded === true;
  const [timedOut, setTimedOut] = useState<number | undefined>(undefined);
  if (pending && failure?.slotId === pending.id) {
    setSlots({ key: slots.key, shown: slots.shown });
    setFailure(undefined);
  } else if (pending && (pendingReady || timedOut === pending.id)) {
    setSlots({ key: slots.key, shown: pending });
    setStatuses(pendingStatus ? { [pending.id]: pendingStatus } : {});
    setPreview(NO_PREVIEW);
  }
  useEffect(() => {
    if (!pending) return;
    const timer = window.setTimeout(() => setTimedOut(pending.id), SWAP_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);

  const shownId = slots.shown.id;
  // Report the shown document's height, fit and images, once each.
  const shownStatus = statuses[shownId];
  // Until the first document reports, the frame reserves an estimate of the
  // markup rather than the 48-px floor (#170). Later messages keep the shown
  // document on screen until the next one has its size, so they never need it.
  const reserved = useMemo(() => estimateFrameHeight(html), [html]);
  const shownHeight = shownStatus?.sized ? shownStatus.height : reserved;
  useEffect(() => {
    if (shownStatus?.sized) callbacks.current.onHeight?.(shownStatus.height);
  }, [shownStatus?.sized, shownStatus?.height]);
  const shownFit = shownStatus?.fit;
  useEffect(() => {
    if (shownFit) callbacks.current.onFit?.(shownFit);
  }, [shownFit]);
  const shownSubresources = shownStatus?.subresources;
  useEffect(() => {
    if (shownSubresources) callbacks.current.onSubresources?.(shownSubresources);
  }, [shownSubresources]);

  if (refused) {
    return (
      <p role="status" className="message-frame-refused">
        This message could not be displayed safely.
      </p>
    );
  }

  const shownFailure = failure?.slotId === shownId ? failure : undefined;
  if (shownFailure) {
    const text = plainTextOf(bodyText, html);
    return (
      <div className="message-frame-fallback">
        <p role="status" className="message-frame-fallback__notice">
          {shownFailure.offline
            ? 'You’re offline, so this message is shown as plain text.'
            : 'Couldn’t display this message’s formatting, so it is shown as plain text.'}
          {!shownFailure.offline && (
            <>
              {' '}
              <button
                type="button"
                className="btn btn--ghost message-frame-fallback__retry"
                onClick={() => setAttempt((n) => n + 1)}
              >
                Try again
              </button>
            </>
          )}
        </p>
        {text.split(/\n{2,}/).map((paragraph, index) => (
          <p key={index} className="message-frame-fallback__text">
            {paragraph}
          </p>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="message-frame-stack" style={{ position: 'relative' }}>
        <IsolatedSlot
          key={slots.shown.id}
          slot={slots.shown}
          visible
          appearance={appearance}
          height={shownHeight}
          fit={fit}
          isolation={isolation}
          onEvent={onSlotEvent}
        />
        {slots.pending && (
          <IsolatedSlot
            key={slots.pending.id}
            slot={slots.pending}
            visible={false}
            appearance={appearance}
            height={pendingStatus?.sized ? pendingStatus.height : shownHeight}
            fit={fit}
            isolation={isolation}
            onEvent={onSlotEvent}
          />
        )}
      </div>
      {preview.length > 0 && <LinkPreview urls={preview} />}
      {confirmLink && (
        <LinkConfirm
          url={confirmLink.url}
          warnings={confirmLink.warnings}
          returnFocusTo={confirmLink.opener}
          onCancel={() => setConfirmLink(undefined)}
          onOpen={() => {
            const url = confirmLink.url;
            setConfirmLink(undefined);
            (callbacks.current.onOpenLink ?? defaultOpenLink)(url);
          }}
        />
      )}
    </>
  );
}

interface IsolatedSlotProps {
  slot: Slot;
  visible: boolean;
  appearance: FrameAppearance;
  height: number;
  fit: FrameFit;
  isolation: FrameIsolation;
  onEvent: (slotId: number, event: SlotEvent) => void;
}

/**
 * One frame: mints its render, checks itself, points itself at it, and turns
 * what the frame says into events. Everything read from the frame is checked
 * three ways before it is an event: it came from THIS frame's window, from an
 * opaque origin (the string "null": the sandbox withholds same-origin), and it
 * is exactly one of the protocol's shapes on this frame's channel.
 */
function IsolatedSlot({ slot, visible, appearance, height, fit, isolation, onEvent }: IsolatedSlotProps): JSX.Element {
  const frameRef = useRef<HTMLIFrameElement>(null);
  useFrameFocusRing(frameRef);
  const [channel] = useState(newChannel);
  const [ticket, setTicket] = useState<{ renderId: string; src: string } | undefined>(undefined);
  const renderId = ticket?.renderId;
  const sizedRef = useRef(false);
  const fitRef = useRef(fit);
  const isolationRef = useRef(isolation);
  const onEventRef = useRef(onEvent);
  const visibleRef = useRef(visible);
  useEffect(() => {
    onEventRef.current = onEvent;
    visibleRef.current = visible;
  });
  const slotId = slot.id;

  const postFit = useCallback(() => {
    const target = frameRef.current?.contentWindow;
    if (!target) return;
    try {
      // '*' because the document's origin is opaque: there is no name to
      // address it by. The message is a fit mode and nothing else.
      target.postMessage(fitMessage(channel, fitRef.current), '*');
    } catch {
      // The frame went away.
    }
  }, [channel]);

  // Mint. Once per frame: a document capability is single-use.
  useEffect(() => {
    const controller = new AbortController();
    const emit = (event: SlotEvent) => onEventRef.current(slotId, event);
    isolationRef.current.mint(controller.signal).then(
      (minted) => {
        if (controller.signal.aborted) return;
        setTicket({ renderId: minted.renderId, src: frameURL(minted.url, channel) });
      },
      (cause: unknown) => {
        if (controller.signal.aborted) return;
        if (cause instanceof ApiError && cause.status === 501) {
          // The server has no user-content origin (any more): srcdoc.
          emit({ type: 'legacy' });
          return;
        }
        emit({ type: 'failed', offline: isOffline(cause) });
      },
    );
    return () => controller.abort();
  }, [channel, slotId]);

  // Listen to this frame, and only to this frame.
  useEffect(() => {
    if (renderId === undefined) return;
    const controller = new AbortController();
    const emit = (event: SlotEvent) => onEventRef.current(slotId, event);
    const onMessage = (event: MessageEvent) => {
      const frame = frameRef.current;
      if (!frame || event.source !== frame.contentWindow || event.origin !== 'null') return;
      const message: FrameMessage | null = parseFrameMessage(event.data, channel);
      if (!message) return;
      switch (message.type) {
        case 'size':
          if (!sizedRef.current) {
            sizedRef.current = true;
            // The frame starts at 'width'; tell it what this pane wants.
            if (fitRef.current !== 'width') postFit();
          }
          emit(message);
          return;
        case 'loaded': {
          const base = { type: 'loaded' as const, requested: message.images };
          if (message.failed === 0) {
            emit({ ...base, failed: 0, refused: 0, svgBlocked: 0 });
            return;
          }
          isolationRef.current.stats(renderId, controller.signal).then(
            (stats) =>
              emit({
                ...base,
                failed: stats.failed,
                refused: stats.refused,
                svgBlocked: stats.svgBlocked,
              }),
            () => {
              if (!controller.signal.aborted) {
                emit({ ...base, failed: message.failed, refused: 0, svgBlocked: 0 });
              }
            },
          );
          return;
        }
        case 'open':
          emit(message.text !== undefined ? { type: 'open', url: message.url, text: message.text } : { type: 'open', url: message.url });
          return;
        case 'mailto':
          emit({ type: 'mailto', href: message.href });
          return;
        case 'preview':
          emit({ type: 'preview', urls: message.urls });
          return;
        case 'fragment':
          // A same-document link (ADR 0101): this frame is as tall as the
          // message, so the pane scrolls, not the frame. Only the document
          // on screen moves anything.
          if (visibleRef.current) scrollFrameTo(frame, message.at);
          return;
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      controller.abort();
      window.removeEventListener('message', onMessage);
    };
  }, [renderId, channel, slotId, postFit]);

  // Check, then load: after the listener above is in place, so the frame's
  // first word is never missed. Set imperatively, after the check, so nothing
  // but a checked URL is ever this element's src.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || ticket === undefined) return;
    try {
      assertIsolatedFrame(frame, ticket.src, isolationRef.current.contentOrigin);
    } catch (err) {
      console.error(err);
      onEventRef.current(slotId, { type: 'refused' });
      return;
    }
    frame.src = ticket.src;
  }, [ticket, slotId]);

  // A frame that loads and never speaks (its script refused, say) gets the
  // documented degraded height, and counts as settled so a swap can finish.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || renderId === undefined) return;
    let timer = 0;
    const onLoad = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => onEventRef.current(slotId, { type: 'silent' }), SILENT_FRAME_MS);
    };
    frame.addEventListener('load', onLoad);
    return () => {
      frame.removeEventListener('load', onLoad);
      window.clearTimeout(timer);
    };
  }, [renderId, slotId]);

  // A fit change re-measures the live document; it never reloads it.
  useEffect(() => {
    if (fitRef.current === fit) return;
    fitRef.current = fit;
    if (sizedRef.current) postFit();
  }, [fit, postFit]);

  return (
    <iframe
      ref={frameRef}
      title={visible ? 'Message body' : 'Message body (loading)'}
      sandbox={ISOLATED_FRAME_SANDBOX}
      referrerPolicy="no-referrer"
      scrolling="no"
      loading="eager"
      className="message-frame"
      data-isolated=""
      {...(visible ? {} : { 'aria-hidden': true, tabIndex: -1 })}
      style={{
        display: 'block',
        width: '100%',
        height: `${height}px`,
        border: 0,
        ...frameCanvas(appearance),
        ...(visible
          ? {}
          : { position: 'absolute', top: 0, left: 0, opacity: 0, pointerEvents: 'none' }),
      }}
    />
  );
}
